import React, { useMemo } from 'react'

import { Button, ButtonProps } from 'rebass'
import SText from 'components/opinionless/SText'

import { Icon } from '@material-ui/core'
import { useHover } from 'utils/customHooks'
import {
    getFButtonHoverStyle,
    FButtonType,
    FButtonTypeEnum,
    getFButtonStyle,
    getFButtonTextStyle,
    getFButtonLoadingTextStyle,
} from './constants'
import SLoadingIndicator from 'components/opinionless/SLoadingIndicator'
import ConditionalRender from 'utils/conditionalRender'
import { FButtonDefaultStyle, FButtonDefaultTextStyle } from './styles'
import { useSTheme } from 'styles/theme'
import { input } from '../constants'
import { ga4_buttonClicked } from 'utils/ga4'

export interface FButtonProps extends ButtonProps {
    loading?: boolean
    buttonType?: FButtonType
    textStyle?: any
    googleIcon?: string
    textLineHeight?: string
    ga4_key?: string
}

const FButton: React.FC<FButtonProps> = (props: FButtonProps) => {
    const {
        loading = props.loading,
        buttonType = FButtonTypeEnum.Filled,
        sx = {},
        textStyle: textStyleProp = {},
        ref,
        googleIcon,
        textLineHeight,
        children,
        onClick,
        ...otherProps
    } = props
    const theme = useSTheme()

    const [hovering, hoverProps] = useHover()

    const containerStyle = useMemo(() => (hovering ? getFButtonHoverStyle : getFButtonStyle)(buttonType, theme), [
        buttonType,
        hovering,
        theme,
    ])

    const textStyle = useMemo(() => getFButtonTextStyle(buttonType, theme), [buttonType, theme])

    const loadingStyle = useMemo(() => getFButtonLoadingTextStyle(buttonType, theme), [buttonType, theme])
    const fontFamily = theme.fontsV2[window.ORGANIZATION.name]?.body || theme.fontsV2.default

    return (
        <Button
            sx={{
                ...containerStyle,
                ...FButtonDefaultStyle,
                marginTop: props.type === 'submit' ? input.desktop.marginTop : 'initial',
                ...sx,
                padding: `${buttonType === FButtonTypeEnum.Link ? 0 : '0 24px'}`,
                width: props.type === 'submit' ? '100%' : 'auto',
                cursor: props.disabled ? 'default' : 'pointer',
            }}
            disabled={loading || props.disabled}
            onClick={(e) => {
                onClick?.(e)
                ga4_buttonClicked(props.ga4_key)
            }}
            {...otherProps}
            {...hoverProps}
        >
            {loading && <SLoadingIndicator size={22} sx={{ ...loadingStyle }} />}
            <SText
                sx={{
                    fontFamily: fontFamily,
                    ...textStyle,
                    ...FButtonDefaultTextStyle,
                    ...textStyleProp,
                    lineHeight: textLineHeight,
                }}
            >
                <ConditionalRender if={googleIcon}>
                    <Icon style={{ verticalAlign: 'middle', marginTop: '-5px', marginRight: '5px' }}>{googleIcon}</Icon>
                </ConditionalRender>
                {!loading && children}
            </SText>
        </Button>
    )
}

export default FButton
